import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  MenuItem,
  Select,
  AppBar,
  Toolbar,
  Paper,
  TextField,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
} from '@mui/material';
import LoadingBar from 'react-top-loading-bar';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddAllTopic = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [teacherId, setTeacherId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [topicOrder, setTopicOrder] = useState('');
  const [topicName, setTopicName] = useState('');
  const [teacherData, setTeacherData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [topics, setTopics] = useState([]);
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const navigate = useNavigate();

  // Fetch Branches Data
    const fetchBranches = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });

    if (response.ok) {
      const data = await response.json();
      setBranches(data);
    } else {
      console.error("Failed to fetch branches");
    }
  } catch (error) {
    console.error("Error fetching branches:", error);
  }
};

useEffect(() => {
  const isMasterUser = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(isMasterUser);

  if (!isMasterUser) {
    const branchId = sessionStorage.getItem("branchId");
    setSelectedBranchId(branchId);
    return;
  }

  // For master users
  fetchBranches().then(() => {
    // After branches are loaded → auto-select first one if nothing is selected
    setBranches((prevBranches) => {
      if (prevBranches.length > 0 && !selectedBranchId) {
        const defaultBranch = prevBranches[0];
        setSelectedBranchId(defaultBranch.branchId);
        // Optional: you can show a small info once
        // setSnackbarMessage(`Auto-selected branch: ${defaultBranch.branchName}`);
        // setOpenSnackbar(true);
        // setSnackbarSeverity('info');
      }
      return prevBranches;
    });
  });
}, []);

// Auto-select first branch when branches are loaded (for master users only)
useEffect(() => {
  if (!isMaster || branches.length === 0 || selectedBranchId) {
    return;
  }

  // Select the first branch by default
  const defaultBranch = branches[0];
  setSelectedBranchId(defaultBranch.branchId);

  // Optional: feedback
  // setSnackbarMessage(`Default branch selected: ${defaultBranch.branchName}`);
  // setOpenSnackbar(true);
  // setSnackbarSeverity('info');

}, [branches, isMaster, selectedBranchId]);

  // Fetch Teacher Data
  const fetchTeacherData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      if (!branchId) {
      setTeacherData([]);
      setLoadingBarProgress(0);
      return;
    }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetTeacher?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        setTeacherData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch teacher data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setTeacherData([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  useEffect(() => {
  if (selectedBranchId || !isMaster) {
    fetchTeacherData();
    // Reset dependent fields when branch changes
    setTeacherId('');
    setClassId('');
    setSectionId('');
    setSubjectId('');
    setTopics([]);
  }
}, [selectedBranchId, isMaster]);

  // Fetch Class Data
  const fetchClassData = async (selectedTeacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      if (!branchId) {
        setClassData([]);
        setLoadingBarProgress(0);
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetClass?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ teacherId: selectedTeacherId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setClassData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch class data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setClassData([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Fetch Section Data
  const fetchSectionData = async (selectedClassId, selectedTeacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/ddlSection_clsId`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, teacherId: selectedTeacherId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setSectionData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch section data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSectionData([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Fetch Subject Data
  const fetchSubjectData = async (selectedClassId, selectedTeacherId, selectedSectionId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
        if (!branchId) {
          setSubjectData([]);
          setLoadingBarProgress(0);
          return;
        }
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/Subjectddl?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId, teacherId: selectedTeacherId, sectionId: selectedSectionId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setSubjectData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch subject data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSubjectData([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Fetch Teacher Data on Component Mount
  useEffect(() => {
    fetchTeacherData();
  }, []);

  // Fetch Class Data when Teacher is Selected
  useEffect(() => {
    if (teacherId) {
      fetchClassData(teacherId);
      setClassId('');
      setSectionId('');
      setSubjectId('');
      setTopics([]);
    }
  }, [teacherId]);

  // Fetch Section Data when Class is Selected
  useEffect(() => {
    if (classId && teacherId) {
      fetchSectionData(classId, teacherId);
      setSectionId('');
      setSubjectId('');
      setTopics([]);
    }
  }, [classId, teacherId]);

  // Fetch Subject Data when Section is Selected
  useEffect(() => {
    if (sectionId && classId && teacherId) {
      fetchSubjectData(classId, teacherId, sectionId);
      setSubjectId('');
      setTopics([]);
    }
  }, [sectionId, classId, teacherId]);

  const handleAddTopic = () => {
    if (!topicOrder || !topicName) {
      setSnackbarMessage('Please enter both Topic Order and Topic Name');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      return;
    }

    const newTopic = {
      topicOrder: Number(topicOrder),
      topicName,
    };

    setTopics([...topics, newTopic]);
    setTopicOrder('');
    setTopicName('');
  };

  const handleDeleteTopic = (index) => {
    const updatedTopics = [...topics];
    updatedTopics.splice(index, 1);
    setTopics(updatedTopics);
  };

const handleSave = async (e) => {
  e.preventDefault();

  const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");

  if (!branchId) {
    setSnackbarMessage("Branch information is missing. Please select a branch.");
    setOpenSnackbar(true);
    setSnackbarSeverity("error");
    return;
  }

  if (!classId || !sectionId || !subjectId || topics.length === 0) {
    setSnackbarMessage("Please complete all required fields and add at least one topic.");
    setOpenSnackbar(true);
    setSnackbarSeverity("error");
    return;
  }

  try {
    setLoadingBarProgress(30);

    const url = `${process.env.REACT_APP_BASE_URL}/TeachingTopics/SaveList?Branch_Id=${branchId}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: sessionStorage.getItem("token"),
      },
      body: JSON.stringify({
        classId: Number(classId),
        sectionId: Number(sectionId),
        subjectId: Number(subjectId),
        topics,
      }),
    });

    if (response.ok) {
      setLoadingBarProgress(100);
      setShowSuccessModal(true);
    } else {
      let errorMsg = "Unable to save topics.";
      try {
        const errorData = await response.json();
        errorMsg = errorData.message || errorMsg;
      } catch {}
      setSnackbarMessage(errorMsg);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setLoadingBarProgress(0);
    }
  } catch (error) {
    console.error("Save error:", error);
    setSnackbarMessage("An error occurred while saving. Please try again later.");
    setOpenSnackbar(true);
    setSnackbarSeverity("error");
    setLoadingBarProgress(0);
  }
};

  const handleCancel = () => {
    setTeacherId('');
    setClassId('');
    setSectionId('');
    setSubjectId('');
    setTopicOrder('');
    setTopicName('');
    setTopics([]);
  };

 return (
  <Container maxWidth="lg" sx={{ py: 3 }}>
    <LoadingBar 
      progress={loadingBarProgress} 
      onLoaderFinished={() => setLoadingBarProgress(0)} 
      color="rgb(152, 106, 182)" 
      height={5}
    />
    
   <AppBar
  position="static"
  elevation={0}
  sx={{
    backgroundColor: '#0B1F3D',
    borderRadius: '8px 8px 0 0',
    mb: 0,
  }}
>
  <Toolbar
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.5,
      px: 3,
    }}
  >
    {/* Left: Title */}
    <Typography
      variant="h4"
      component="div"
      sx={{
        fontWeight: 600,
        letterSpacing: '0.5px',
        color: '#ffffff',
      }}
    >
      Add Topics
    </Typography>

    {/* Right: Branch Dropdown (only for master user) */}
    {isMaster && (
      <Box sx={{ position: 'relative' }}>
        <FormControl size="small" sx={{ width: 220 }}>
          <InputLabel
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.75rem',
              fontWeight: 500,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              top: '-2px',
              '&.Mui-focused': { color: '#64B5F6' },
              '&.MuiFormLabel-filled': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            Branch
          </InputLabel>
          <Select
            value={selectedBranchId || ''}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            label="Branch"
            displayEmpty
  renderValue={(selected) => {
    if (!selected) {
      return <em>Select Branch</em>;   // fallback when nothing selected
    }
    const branch = branches.find(b => b.branchId === selected);
    return branch ? branch.branchName : selected;
  }}
            startAdornment={
              <InputAdornment position="start">
                <LocationOnIcon
                  sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }}
                />
              </InputAdornment>
            }
            sx={{
              color: '#ffffff',
              fontWeight: 500,
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              backgroundColor: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(8px)',
              borderRadius: '8px',
              transition: 'background-color 0.2s ease, box-shadow 0.2s ease',

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.15)',
                borderWidth: '1px',
                transition: 'border-color 0.2s ease',
              },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.11)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.35)',
                },
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#64B5F6',
                  borderWidth: '1.5px',
                },
              },
              '& .MuiSelect-icon': {
                color: 'rgba(255,255,255,0.5)',
                transition: 'color 0.2s ease',
              },
              '&.Mui-focused .MuiSelect-icon': {
                color: '#64B5F6',
              },
              '& .MuiSelect-select': {
                py: 1.1,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  borderRadius: '10px',
                  border: '1px solid rgba(11,31,61,0.12)',
                  boxShadow:
                    '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  '& .MuiList-root': { py: 0.5 },
                },
              },
            }}
          >
            {branches.map((branch) => (
              <MenuItem
                key={branch.branchId}
                value={branch.branchId}
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#0B1F3D',
                  px: 2,
                  py: 1.1,
                  mx: 0.5,
                  borderRadius: '6px',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(11,31,61,0.07)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25,118,210,0.1)',
                    color: '#1565C0',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(25,118,210,0.15)',
                    },
                  },
                }}
              >
                {branch.branchName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )}
  </Toolbar>
</AppBar>

    <Paper 
      elevation={3} 
      sx={{ 
        padding: 4,
        width: '100%',
        margin: 'auto',
        borderRadius: '0 0 8px 8px',
        backgroundColor: '#fafafa'
      }}
    >
      <form onSubmit={handleSave} style={{ width: '100%' }}>
        {/* Dropdowns Section */}
        <Box 
          sx={{ 
            backgroundColor: '#ffffff',
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: '#2c3e50',
              fontSize: '1.1rem'
            }}
          >
            Select Details
          </Typography>

          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2.5
            }}
          >
            {/* Teacher Dropdown */}
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="teacher">Teacher</InputLabel>
              <Select
                id="teacher"
                value={teacherId}
                label="Teacher"
                onChange={(e) => setTeacherId(e.target.value)}
                sx={{ 
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0B1F3D',
                  }
                }}
              >
                {teacherData.map((teacher) => (
                  <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                    {teacher.employeeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Class Dropdown */}
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="class">Class</InputLabel>
              <Select
                id="class"
                value={classId}
                label="Class"
                onChange={(e) => setClassId(e.target.value)}
                disabled={!teacherId}
                sx={{ 
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0B1F3D',
                  }
                }}
              >
                {classData.map((classItem) => (
                  <MenuItem key={classItem.classId} value={classItem.classId}>
                    {classItem.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Section Dropdown */}
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="section">Section</InputLabel>
              <Select
                id="section"
                value={sectionId}
                label="Section"
                onChange={(e) => setSectionId(e.target.value)}
                disabled={!classId}
                sx={{ 
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0B1F3D',
                  }
                }}
              >
                {sectionData.map((sectionItem) => (
                  <MenuItem key={sectionItem.sectionId} value={sectionItem.sectionId}>
                    {sectionItem.sectionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Subject Dropdown */}
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="subject">Subject</InputLabel>
              <Select
                id="subject"
                value={subjectId}
                label="Subject"
                onChange={(e) => setSubjectId(e.target.value)}
                disabled={!sectionId}
                sx={{ 
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0B1F3D',
                  }
                }}
              >
                {subjectData.map((subjectItem) => (
                  <MenuItem key={subjectItem.subjectId} value={subjectItem.subjectId}>
                    {subjectItem.subjectName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Topic Entry Section */}
        <Box 
          sx={{ 
            backgroundColor: '#ffffff',
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: '#2c3e50',
              fontSize: '1.1rem'
            }}
          >
            Add Topic
          </Typography>

          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr auto' },
              gap: 2,
              alignItems: 'start'
            }}
          >
            <TextField
              fullWidth
              id="topicOrder"
              label="Topic Order"
              variant="outlined"
              type="number"
              value={topicOrder}
              onChange={(e) => setTopicOrder(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#0B1F3D',
                  }
                }
              }}
            />
            <TextField
              fullWidth
              id="topicName"
              label="Topic Name"
              variant="outlined"
              value={topicName}
              onChange={(e) => setTopicName(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#0B1F3D',
                  }
                }
              }}
            />
            <Button
              variant="contained"
              color='primary'
              onClick={handleAddTopic}
              sx={{ 
                height: 56,
                px: 4,
                fontWeight: 600,
                fontSize: '0.95rem',
                // backgroundColor: '#0B1F3D',
                // '&:hover': {
                //   backgroundColor: '#0a1a30',
                //   transform: 'translateY(-2px)',
                //   boxShadow: '0 4px 12px rgba(11, 31, 61, 0.3)',
                // },
                // transition: 'all 0.3s ease'
              }}
            >
              Add
            </Button>
          </Box>
        </Box>

        {/* Topics Table */}
        {topics.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600, 
                color: '#2c3e50',
                fontSize: '1.1rem'
              }}
            >
              Added Topics ({topics.length})
            </Typography>
            
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
            >
              <Table
                sx={{
                  '& .MuiTableCell-root': {
                    fontSize: '0.95rem',
                    padding: '14px 16px',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 700,
                    backgroundColor: '#f8f9fa',
                    color: '#2c3e50',
                    borderBottom: '2px solid #dee2e6',
                    textTransform: 'uppercase',
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                  },
                  '& .MuiTableBody-root .MuiTableRow-root': {
                    '&:hover': {
                      backgroundColor: '#f5f7fa',
                      transition: 'background-color 0.2s ease',
                    }
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '20%' }}>Topic Order</TableCell>
                    <TableCell sx={{ width: '65%' }}>Topic Name</TableCell>
                    <TableCell sx={{ width: '15%', textAlign: 'center' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topics.map((topic, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafbfc'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B1F3D' }}>
                          {topic.topicOrder}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#495057' }}>
                          {topic.topicName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="Remove topic" arrow>
                          <IconButton 
                            onClick={() => handleDeleteTopic(index)}
                            sx={{
                              color: '#dc3545',
                              '&:hover': {
                                backgroundColor: '#ffebee',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Action Buttons */}
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 2,
            mt: 4,
            p: 3,
            backgroundColor: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
         <Tooltip
  title={
    !teacherId ? 'Please select a teacher' :
    !classId ? 'Please select a class' :
    !sectionId ? 'Please select a section' :
    !subjectId ? 'Please select a subject' :
    topics.length === 0 ? 'Please add at least one topic' :
    (isMaster && !selectedBranchId) ? 'Please select a branch' :
    ''
  }
  arrow
>
            <span>
              <Button
  type="submit"
  variant="contained"
  disabled={
    !teacherId ||
    !classId ||
    !sectionId ||
    !subjectId ||
    topics.length === 0 ||
    (isMaster && !selectedBranchId) ||
    (!isMaster && !sessionStorage.getItem("branchId"))
  }
                sx={{
                  px: 5,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  letterSpacing: '0.5px',
                  backgroundColor: '#28a745',
                  minWidth: '140px',
                  '&:hover': {
                    backgroundColor: '#218838',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                  },
                  transition: 'all 0.3s ease',
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                  }
                }}
              >
                SAVE
              </Button>
            </span>
          </Tooltip>
          
          <Button 
            type="button" 
            variant="outlined"
            onClick={handleCancel}
            sx={{
              px: 5,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1rem',
              letterSpacing: '0.5px',
              borderColor: '#dc3545',
              color: '#dc3545',
              minWidth: '140px',
              '&:hover': {
                borderColor: '#c82333',
                backgroundColor: '#fff5f5',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            RESET
          </Button>
        </Box>
      </form>
    </Paper>

    {/* Success Modal */}
   <Modal
  open={showSuccessModal}
  onClose={() => setShowSuccessModal(false)}
  aria-labelledby="success-modal-title"
  aria-describedby="success-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography
        id="success-modal-title"
        variant="h6"
        sx={{
          color: '#fff',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        Topics Added <Typography sx={{ color: '#4caf50', fontSize: '1.2rem' }}>✓</Typography>
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography
        id="success-modal-description"
        sx={{
          mb: 2,
          fontSize: '1rem',
          color: '#495057',
          textAlign: 'center',
          lineHeight: 1.6,
        }}
      >
        Topics have been added successfully!
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            navigate(-1);
          }}
          variant="contained"
          sx={{
            backgroundColor: '#0B1F3D',
            '&:hover': {
              backgroundColor: '#0a1a30',
            },
          }}
        >
          OK
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>


    {/* Snackbar for Notifications */}
    <Snackbar
      open={openSnackbar}
      autoHideDuration={4000}
      onClose={() => setOpenSnackbar(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        onClose={() => setOpenSnackbar(false)} 
        severity={snackbarSeverity}
        sx={{ 
          width: '100%',
          borderRadius: 2,
          fontWeight: 500,
          fontSize: '0.95rem'
        }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </Container>
);
};

export default AddAllTopic;
